// Boundary create/edit component

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { theme_bgColorLight1, theme_textColorMain, theme_textColorBlended, theme_bgColorGradient2, theme_bgColorLight, theme_limeGreen } from "../../Theme";
import CloseIcon from '@mui/icons-material/Close';
import { IOrgManagement_Boundary, IOrgManagement_Data } from "./OrgManagementInterfaces";
import { ChangeEvent, useEffect, useState } from "react";
import { ConvertFileToGeoJSON, CreateOrUpdateBoundary, GetBoundaryUseCount } from "./OrgManagementOps";
import { CustomTextField } from "../../LayerLibrary/EditLayer/EditLayer";
import { ToastNotification } from "../../ToastNotifications";
import MapboxComponent from "./MapboxComponent";
import { FriendlyLargeNumber, FriendlyNumber } from "../../Globals";
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { MultiPolygon } from 'geojson';
import { TAoiUploadFileFormat } from "../../Aois/AoiUpload";
import { GetPolygonCount, GetGeojsonPointCount, GetGeojsonAcres } from "../../GisOps";


const BOUNDARY_NAME_MAX_LENGTH: number = 50;
const BOUNDARY_FILE_MAX_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface BoundaryEditorProps
{
  showBoundaryEditor: boolean;
  setShowBoundaryEditor: any;
  editBoundary?: IOrgManagement_Boundary;   // If this is undefined, we are creating a new boundary
  orgData?: IOrgManagement_Data;
  NotifyCreatedOrUpdatedBoundary: any;
}

//-------------------------------------------------------------------------------
// Boundary create/edit component
//-------------------------------------------------------------------------------
const BoundaryEditor = (props: BoundaryEditorProps) => 
{
  const [localBoundary, setLocalBoundary] = useState<IOrgManagement_Boundary|undefined>(undefined);
  const [createNewMode, setCreateNewMode] = useState<boolean>(false);
  const [changesWereMade, setChangesWereMade] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);





  //-------------------------------------------------------------------------------
  // Init
  //-------------------------------------------------------------------------------
  useEffect(() => 
  {
    // Create a local copy of the boundary being edited every time the window is opened

    if(props.showBoundaryEditor === true)
    {
      if(props.editBoundary) // EDIT MODE
      {
        setLocalBoundary(props.editBoundary);
        setCreateNewMode(false);
        setChangesWereMade(false);
      }
      else // CREATE NEW MODE
      {
        setLocalBoundary(
        {
          id: null,  // new boundary - don't know ID until API call is made to return it
          name: '',
          geom: null,
          settings: 
          {
            states: []
          }
        });
        setCreateNewMode(true);
        setChangesWereMade(true);
      }
    }

  }, [props.showBoundaryEditor])  

  //-------------------------------------------------------------------------------
  // Close/cancel window.
  //-------------------------------------------------------------------------------
  const OnClose = () => 
  {
    // Disable closing the window if a file is currently being uploaded
    if(uploadInProgress) return;

    props.setShowBoundaryEditor(false);
    setLocalBoundary(undefined);
  }

  //-------------------------------------------------------------------------------
  // Accept/OK.
  //-------------------------------------------------------------------------------
  async function OnAccept()
  {
    if(!localBoundary || !props.orgData) return;

    if(changesWereMade === false)
    {
      OnClose();
      return;
    }

    // NOTE:  A boundary of -1 means "no boundary"

    // Validate

    localBoundary.name = localBoundary.name.trim();
    if(localBoundary.name.length === 0)
    {
      ToastNotification('error', 'The boundary name cannot be empty');
      return;
    }

    if(localBoundary.name.length < 3)
    {
      ToastNotification('error', 'The boundary name must be at least 3 characters long');
      return;
    }
      
    // If we're creating a new boundary, the boundary name cannot already exist

    if(createNewMode && props.orgData.boundaries.find(b => b.name.toLowerCase().trim() === localBoundary.name.toLocaleLowerCase().trim()) !== undefined)
    {
      ToastNotification('error', 'An boundary with that name already exists');
      return;
    }

    if(!localBoundary.geom || localBoundary.geom.type !== 'MultiPolygon')
    {
      ToastNotification('error', 'Invalid or empty boundary geometry');
      return;
    }
    
    // Call server

    const data: {ret: boolean; new_id?: number} = await CreateOrUpdateBoundary(localBoundary);
    if(data.ret === false)
      return;

    // Notify caller
    //
    // If we created a new boundary, update the ID.

    if(createNewMode)
      props.NotifyCreatedOrUpdatedBoundary({...localBoundary, id: data.new_id}, true);
    else // edit mode
      props.NotifyCreatedOrUpdatedBoundary(localBoundary, false);

    // Close this window
    OnClose();
  }

  //-------------------------------------------------------------------------------
  // The boundary name text field has changed.
  //-------------------------------------------------------------------------------
  function OnBoundaryNameChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!localBoundary) return;

    const newValue: string = event.target.value;

    const updatedBoundary: IOrgManagement_Boundary =
    {
      ...localBoundary,
      name: newValue
    }

    setLocalBoundary(updatedBoundary);
    setChangesWereMade(true);
  }

  //-------------------------------------------------------------------------------
  // The user has selected a file to upload.
  //-------------------------------------------------------------------------------
  const OnUploadFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => 
  {
    if(!localBoundary || !event.target.files || event.target.files.length === 0) return;

    const file: File = event.target.files[0];
    if(!file) return;

    if(!file.name) return;  // If the user cancels

    if(file.size > BOUNDARY_FILE_MAX_SIZE_BYTES)
    {
      ToastNotification('error', `The file you selected is too large (over ${BOUNDARY_FILE_MAX_SIZE_BYTES} MB)`);
      return;
    }

    // Determine the file format (based on the file extension)

    const fileExt: string = file.name.split(".").pop()?.toLowerCase() || '';
    let format: TAoiUploadFileFormat = fileExt as TAoiUploadFileFormat;
    if(!format || format.length === 0)
      format = 'geojson';

    // Call server to convert the local file into a normalized GeoJSON/MultiPolygon boundary

    const newGeojsonBoundary: MultiPolygon | undefined = await ConvertFileToGeoJSON(file, format, setUploadInProgress) as MultiPolygon;
    if(!newGeojsonBoundary)
    {
      ToastNotification('error', `Unable to load boundary from provided file`);
      return;
    }
  
    // Update the local boundary

    const updatedBoundary: IOrgManagement_Boundary =
    {
      ...localBoundary,
      geom: newGeojsonBoundary,

      polygons: GetPolygonCount(newGeojsonBoundary),
      points: GetGeojsonPointCount(newGeojsonBoundary),
      acres: GetGeojsonAcres(newGeojsonBoundary),
    }

    setLocalBoundary(updatedBoundary);
    setChangesWereMade(true);
  }

  















  if(!props.orgData || !localBoundary) return null;

  //-------------------------------------------------------------------------------
  // Main render
  //-------------------------------------------------------------------------------

  return (
    
    <Dialog disablePortal 
            open={props.showBoundaryEditor === true}
            onClose={OnClose} maxWidth='md'
            PaperProps={{ sx: { minHeight: '30vh', maxHeight: '90vh' }}}>

      {/* Dialog Title */}

      <DialogTitle sx={{ bgcolor: theme_bgColorLight1, justifyContent: 'space-between', pl: 2, pr: 1 }}>

        <Stack direction='row' sx={{ justifyContent: 'space-between' }}>

          <Typography sx={{ fontSize: '1.3rem', fontWeight:' bold', color: theme_textColorMain }}>
            { createNewMode ? 'Create a New Boundary': 'Edit Boundary' }
          </Typography>

          <IconButton size="small" onClick={OnClose}
                      sx={{ ml: 12, padding: 0, width: '35px', height: '35px' }}>
            <CloseIcon sx={{ opacity: 0.9, width: '35px', height: '35px', color: theme_textColorBlended }} />
          </IconButton>

        </Stack>

      </DialogTitle>

      {/* Dialog Content */}

      <DialogContent sx={{ background: theme_bgColorGradient2 }}>
        <Stack sx={{ mt: 2, alignItems: 'left' }}>

          {/* Stats */}

          <Stack direction='column'>

            {/* Boundary use count */}

            {createNewMode !== true
              ?
                <Stack direction='row' sx={{ mb: 2, alignItems: 'center' }}>
                  <Typography sx={{ ml: 0, fontWeight: 'bold', color: theme_textColorMain, opacity: 0.7, fontSize: '1.0rem' }}>
                    {GetBoundaryUseCount(props.orgData, localBoundary.id)}
                  </Typography>
                  <Typography sx={{ ml: 0.5, color: theme_textColorBlended, opacity: 0.8, fontSize: '0.8rem' }}>
                    {GetBoundaryUseCount(props.orgData, localBoundary.id) === 1 ? 'organization uses this boundary' : 'organizations use this boundary'}
                  </Typography>
                </Stack>
              :null
            }

            <Stack direction='row' sx={{ mb: 2 }}>

              {/* Polygon count */}

              {localBoundary.polygons !== undefined && localBoundary.polygons >= 0
                ?
                  <Stack direction='row' sx={{ mr: 4, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 'bold', color: theme_textColorMain, opacity: 0.7, fontSize: '1.0rem' }}>
                      {localBoundary.polygons}
                    </Typography>
                    <Typography sx={{ ml: 0.5, color: theme_textColorBlended, opacity: 0.8, fontSize: '0.8rem' }}>
                      polygon{localBoundary.polygons===1?'':'s'}
                    </Typography>
                  </Stack>
                :null
              }

              {/* Point count */}

              {localBoundary.points !== undefined && localBoundary.points >= 0
                ?
                  <Stack direction='row' sx={{ mr: 4, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 'bold', color: theme_textColorMain, opacity: 0.7, fontSize: '1.0rem' }}>
                      {FriendlyNumber(localBoundary.points)}
                    </Typography>
                    <Typography sx={{ ml: 0.5, color: theme_textColorBlended, opacity: 0.8, fontSize: '0.8rem' }}>
                      points
                    </Typography>
                  </Stack>
                :null
              }

              {/* Point count */}

              {localBoundary.acres !== undefined && localBoundary.acres >= 0
                ?
                  <Stack direction='row' sx={{ mr: 4, alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 'bold', color: theme_textColorMain, opacity: 0.7, fontSize: '1.0rem' }}>
                      {FriendlyLargeNumber(localBoundary.acres)}
                    </Typography>
                    <Typography sx={{ ml: 0.5, color: theme_textColorBlended, opacity: 0.8, fontSize: '0.8rem' }}>
                      acres
                    </Typography>
                  </Stack>
                :null
              }              
            </Stack>

          </Stack>

          {/* Name */}

          <CustomTextField variant='standard' size='small' autoComplete='off'
                            value={localBoundary.name}
                            onChange={OnBoundaryNameChanged}
                            inputProps={{ maxLength: BOUNDARY_NAME_MAX_LENGTH }}
                            label=
                              {
                                <Typography sx={{ fontSize:'0.8rem',color: theme_textColorBlended+'B0' }}>
                                  Boundary Name
                                </Typography>
                              }
                              sx={{ mt: 0, p: 0, width: '100%' }}/>


          {/* Mapbox preview */}

          <Typography sx={{ mt: 2, fontSize: '0.8rem', color: theme_textColorBlended }}>
            Map Preview
          </Typography>

          <Stack sx={{ mt: 0.5, height: '350px', width: '500px', bgcolor: theme_bgColorLight+'A0', p: '2px', 
                       borderRadius: 0, justifyContent: 'center', alignItems: 'center' }}>

            {uploadInProgress
              ?
                <CircularProgress/>
              :
                <MapboxComponent geom={localBoundary.geom}/>
            }
          </Stack>

          {/* File upload controls */}

          <Stack sx={{ mx: 2, mt: 0 }}>

            <Stack sx={{ width: '100%', alignItems: 'center', my: 0 }}>

              <label htmlFor="upload-boundary-file">
                <input style={{ display: 'none' }} id="upload-boundary-file" name="upload-boundary-file" type="file" 
                        onChange={OnUploadFileSelected} accept=".geojson, .json, application/geo+json, .kml, .kmz, .zip, .gpkg"/>

                  <Button variant='text' component="span" disabled={uploadInProgress}
                          sx={{ mt: 1, alignItems: 'center', textTransform: 'none', px: '4px' }}>
                    <DriveFolderUploadIcon sx={{ color: theme_limeGreen, width: '36px', height: '36px', mr: '8px' }}/>
                    <Stack>
                      <Typography sx={{ color: theme_limeGreen, fontSize: '1.1rem' }}>
                        Load boundary from a local file
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: theme_textColorBlended, opacity: 0.8 }}>
                        GeoJSON, Shapefile (ZIP), KML, KMZ, GeoPackage
                      </Typography>
                    </Stack>
                  </Button>
              </label>

            </Stack>
          </Stack>

        </Stack>
      </DialogContent>

      {/* Dialog bottom bar */}

      <DialogActions sx={{ bgcolor: theme_bgColorLight1 }}>

      <Stack direction='column' sx={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          
          {/* CANCEL and ACCEPT CHANGES buttons */}

          <Stack direction='row'>
            <Stack sx={{ alignItems: 'center' }}>
              <Button variant='outlined' onClick={OnClose} sx={{ mr: 3, width: '100px' }}>
                Cancel
              </Button>
            </Stack>

            <Stack sx={{ alignItems: 'center' }}>
              <Button type="submit" variant='contained' sx={{ width: '250px', fontWeight: 'bold', textTransform: 'none' }}
                      onClick={_=>OnAccept()}>
                {createNewMode ? 'Create New Boundary': 'Accept Changes' }
              </Button>
            </Stack>
          </Stack>

        </Stack>

      </DialogActions>
            
    </Dialog>          
  )
}


export default BoundaryEditor;

